import { Component, inject, signal } from '@angular/core'
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { ToastrService } from 'ngx-toastr'
import { ButtonComponent } from '../form-elements/button/button.component'
import { InputTextareaComponent } from '../form-elements/input-textarea/input-textarea.component'
import { AiRewriteService, AiTextGenerationResponse } from 'src/app/core/services/ai/ai-rewrite.service'
import { NgSelectModule } from '@ng-select/ng-select'


export interface AiTextGeneratorConfig {
    instructions?: string
    placeholder?: string
    title?: string
    maxLength: number
    initialPrompt?: string
}

@Component({
    selector: 'sf-ai-text-generator-modal',
    standalone: true,
    imports: [NgSelectModule, ReactiveFormsModule, ButtonComponent, InputTextareaComponent],
    templateUrl: './ai-text-generator-modal.component.html',
    styleUrls: ['./ai-text-generator-modal.component.scss'],
})
export class AiTextGeneratorModalComponent {
    #activeModal = inject(NgbActiveModal)
    #fb = inject(FormBuilder)
    #aiService = inject(AiRewriteService)
    #toastr = inject(ToastrService)

    config: AiTextGeneratorConfig = {
        title: 'AI Text Generator',
        placeholder: 'Describe what you want to generate...',
        maxLength: 500,
    }

    isGenerating = signal(false)
    hasGenerated = signal(false)

    form = this.#fb.group({
        prompt: this.#fb.nonNullable.control<string>('', [Validators.required, Validators.minLength(10)]),
        generatedText: this.#fb.nonNullable.control<string>(''),
        style: this.#fb.nonNullable.control<1 | 2 | 3>(1),
    })

    ngOnInit() {
        if (this.config.initialPrompt) {
            this.form.patchValue({ prompt: this.config.initialPrompt })
        }
    }

    getPlaceholder(): string {
        return this.config.placeholder || 'Describe what you want to generate...'
    }

    generate() {
        if (this.form.controls.prompt.invalid) {
            this.#toastr.warning('Please enter a valid prompt', 'Validation Error')
            return
        }

        this.isGenerating.set(true)

        const prompt = this.form.controls.prompt.value
        const style = this.form.controls.style.value

        this.#aiService.generateText(prompt, style, this.config.instructions).subscribe({
            next: (generatedText: AiTextGenerationResponse) => {
                this.form.patchValue({ generatedText: generatedText.content })
                this.hasGenerated.set(true)
                this.#toastr.success('Text generated successfully', 'Success')
            },
            error: (error) => {
                console.error('Error generating text:', error)
                this.#toastr.error('Failed to generate text. Please try again.', 'Error')
            },
            complete: () => {
                this.isGenerating.set(false)
            },
        })
    }

    regenerate() {
        this.generate()
    }

    confirm() {
        const generatedText = this.form.controls.generatedText.value
        if (!generatedText) {
            this.#toastr.warning('No text has been generated yet', 'Validation Error')
            return
        }

        this.#activeModal.close(generatedText)
    }

    cancel() {
        this.#activeModal.dismiss()
    }
}
